import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { FlowStepEditorModel, isFixedFinalIndex, isTerminalStepIndex } from '../../models/flow-step-editor.model';
import { datePlusDays } from '../../utils/status-flow-dates';
import { CatalogService } from '@services/data/catalog.service';

@Component({
  selector: 'app-flow-step-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './flow-step-panel.component.html',
  styleUrl: './flow-step-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager
})
export class FlowStepPanelComponent {
  @Input({ required: true }) flow!: FlowStepEditorModel;
  @Input() index = 0;
  @Input() totalSteps = 0;
  @Input() flows: FlowStepEditorModel[] = [];
  @Input() availableStatuses: any[] = [];
  @Input() availableRoles: any[] = [];
  @Input() documentTypes: any[] = [];

  @Output() changed = new EventEmitter<void>();
  @Output() moveUp = new EventEmitter<void>();
  @Output() moveDown = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();

  private catalogService = inject(CatalogService);

  isTerminalStep(): boolean {
    return isTerminalStepIndex(this.totalSteps, this.index);
  }

  isLastStep(): boolean {
    return this.index === this.totalSteps - 1;
  }

  /** Cierre fijo APPROVED: no se mueve, elimina ni cambia de estado. */
  isFixedFinal(): boolean {
    return isFixedFinalIndex(this.flows, this.index);
  }

  isNextFixedFinal(): boolean {
    return isFixedFinalIndex(this.flows, this.index + 1);
  }

  emitChanged(): void {
    this.changed.emit();
  }

  onFlowStatusChange(newStatusUuid: string): void {
    if (this.isFixedFinal()) return;
    this.flow.statusUuid = newStatusUuid;
    const match = this.findStatusByUuid(newStatusUuid);
    this.flow.status = match ?? null;
    this.flow.statusSlug = match?.slug ?? null;
    this.flow.statusId = match?.id ?? null;
    this.emitChanged();
  }

  onAllowsTokenChange(): void {
    if (!this.flow.allowsToken) {
      this.flow.clearTokenFields();
      this.emitChanged();
      return;
    }
    this.flow.applyTokenDefaults(this.nextConfirmSlug());
    this.emitChanged();
  }

  onRequiredRoleChange(): void {
    this.flow.syncRequiredRoleSideEffects();
    this.emitChanged();
  }

  onRequiredRolesChange(): void {
    this.flow.syncRequiredRolesFromArray();
    this.emitChanged();
  }

  onExtraordinaryRoleChange(): void {
    if (!this.flow.alternateRole) this.flow.alternateRoleUntil = null;
    else if (!this.flow.alternateRoleUntil) this.flow.alternateRoleUntil = datePlusDays(30);
    this.emitChanged();
  }

  setExtraordinaryDays(days: number): void {
    if (!this.flow.requiredRoles?.length && !this.flow.requiredRole) return;
    this.flow.alternateRoleUntil = datePlusDays(days);
    this.emitChanged();
  }

  onRequiresDocumentChange(): void {
    if (!this.flow.requiresDocument) {
      this.flow.documentTypeSlug = null;
      this.flow.documentType = null;
    }
    this.emitChanged();
  }

  /** Rol revisor implica Observar; al quitarlo se limpia el destino de subsanar. */
  onContentReviewRoleChange(): void {
    if (this.flow.contentReviewRole) {
      this.flow.allowsObserve = true;
      this.flow.observeToStatusSlug = this.flow.observeToStatusSlug || 'OBSERVED';
      if (!this.flow.resubmitToStatusSlug && this.flow.statusSlug) {
        this.flow.resubmitToStatusSlug = this.flow.statusSlug;
      }
    } else {
      this.flow.allowsObserve = false;
      this.flow.observeToStatusSlug = null;
      this.flow.resubmitToStatusSlug = null;
    }
    this.emitChanged();
  }

  onDocumentTypeChange(newSlug: string): void {
    this.flow.documentTypeSlug = newSlug ?? null;
    this.flow.documentType = this.documentTypes.find((d) => d.slug === newSlug) ?? null;
    this.emitChanged();
  }

  tokenCoversRole(): boolean {
    return !!this.flow?.allowsToken && String(this.flow?.tokenAudience || '').toUpperCase() === 'ROLE';
  }

  stepTitle(): string {
    const status = this.statusName();
    if (this.isTerminalStep()) return `${status} · Cierre del flujo`;
    const primary = this.flow?.requiredRoles?.[0] ?? this.flow?.requiredRole;
    if (!primary) return status;
    const extraCount = (this.flow?.requiredRoles?.length ?? 0) - 1;
    const role = extraCount > 0
      ? `${this.roleLabel(primary)} +${extraCount}`
      : this.roleLabel(primary);
    if (this.flow.isExtraordinaryActive()) {
      return `${status} · ${role} / ${this.roleLabel(this.flow.alternateRole)}`;
    }
    return `${status} · ${role}`;
  }

  statusName(): string {
    if (this.flow?.status?.name) return this.flow.status.name;
    const statusId = this.flow?.statusId;
    if (!statusId) return 'Sin estado';
    return this.catalogService.getStatusById(statusId)?.name
      ?? this.availableStatuses.find((s) => s.id === statusId || s.uuid === statusId)?.name
      ?? 'Sin estado';
  }

  roleLabel(roleName: string | null | undefined): string {
    if (!roleName) return 'Cualquier usuario';
    const role = this.availableRoles.find((r) => (r.slug ?? r.name) === roleName);
    return role?.displayName || role?.slug || role?.name || roleName;
  }

  documentTypeNameBySlug(slug: string | null | undefined): string {
    if (!slug) return '—';
    return this.documentTypes.find((d) => d.slug === slug)?.name ?? slug;
  }

  alternateRolesFor(): any[] {
    const blocked = new Set(this.flow?.requiredRoles ?? []);
    if (this.flow?.requiredRole) blocked.add(this.flow.requiredRole);
    return this.availableRoles.filter((r) => !blocked.has(r.slug ?? r.name));
  }

  minExtraordinaryDate(): string {
    return datePlusDays(0);
  }

  statusesForSelect(): any[] {
    const currentKey = this.flow.statusUuid ?? this.flow.statusId;
    const active = this.availableStatuses;
    if (!currentKey || active.some((s) => s.uuid === currentKey || s.id === currentKey)) {
      return active;
    }
    const missing = this.catalogService.statuses()?.find(
      (s: any) => s.uuid === currentKey || s.id === currentKey
    );
    return missing ? [...active, missing] : active;
  }

  private findStatusByUuid(uuid: string): any {
    return this.availableStatuses.find((s: any) => s.uuid === uuid)
      ?? this.catalogService.statuses()?.find((s: any) => s.uuid === uuid);
  }

  private nextConfirmSlug(): string | null {
    const next = this.flows[this.index + 1];
    const nextStatus = this.availableStatuses.find((s) => s.id === next?.statusId);
    return nextStatus?.slug || null;
  }
}
